import { initializeApp } from 'firebase/app'
import { getAuth, connectAuthEmulator, onAuthStateChanged, useDeviceLanguage, signOut } from 'firebase/auth'
import { getMessaging, getToken } from 'firebase/messaging'
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config'
import axios from 'axios'
import * as Sentry from '@sentry/react'
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core'
import * as routes from '../routes'
import environment from '../environment'

class AuthClient {
  constructor() {
    this.initialized = false
    this.isGoogleAvailable = false
    this.state = {
      user: {},
      beatstarsPassthrough: null,
    }
    this.fcmToken = null
    this.appVersion = process.env.APP_VERSION
  }

  initialize(callback) {
    if (this.initialized) {
      callback()
      return
    }
    this.initialized = true

    // XXX: Update firebase-messaging-sw.js when changing this config
    this.firebaseConfig = environment() === 'prod' ? {
      app: {
        apiKey: 'AIzaSyDKwg7szD3Sfg17cCcwhJwlJKt3J-HVMqk',
        authDomain: 'voloco-156320.firebaseapp.com',
        projectId: 'voloco-156320',
        appId: '1:73035618530:web:7db48d4d628fc0aa2a66bb',
        messagingSenderId: '73035618530'
      },
      messaging: {
        vapidKey: 'BKubYQEEWtDJ9xqUwPW5YNjEujQkyq7irU9w8P-x88gBqdLGBn39Goqqf8x2uco7rGL1ZDMFvU89GsciRVXcCgs'
      }
    } : {
      app: {
        apiKey: 'AIzaSyAKWi76Z7EBLRdOrPg8zwcO5Ye5SxUsceg',
        authDomain: 'voloco-dev.firebaseapp.com',
        projectId: 'voloco-dev',
        appId: '1:173575683994:web:3fbc5e3ced3eaee13b0423',
        messagingSenderId: '173575683994'
      },
      messaging: {
        vapidKey: 'BAx91Jdf7MU_dsZUpdYBtOUsUM_eqCyVAJsBQJeFi9XNceDwUBBpbKHxZr3-hIdf5PXiJuoPcA1h25WkfiWEpFk'
      }
    }

    let app = initializeApp(this.firebaseConfig.app)
    this.auth = getAuth(app)
    this.messaging = getMessaging(app)

    // setup remote config
    this.remoteConfig = getRemoteConfig(app)
    this.remoteConfig.defaultConfig = {
      'boost_beat_sku': 'boost_beat_a'
    }
    fetchAndActivate(this.remoteConfig)
      .catch((error) => {
        Sentry.captureException(error)
      })

    // setup some auth stuff
    if (environment() === 'dev') {
      connectAuthEmulator(this.auth, 'http://localhost:9099')
    }
    useDeviceLanguage(this.auth)

    axios.get(
      'https://apis.google.com/js/api.js'
    ).then((response) => {
      this.isGoogleAvailable = true
    }).catch((error) => {
      this.isGoogleAvailable = false
    }).finally(callback)
  }

  currentUser() {
    return this.state.user
  }

  userId() {
    return this.state.user.user_id
  }

  updateUser(user) {
    const userId = user.user_id || this.userId()
    this.state.user = {
      user_id: userId,
      username: user.username,
      profile: user.profile
    }
    mixpanel.identify(userId)
  }

  tryLogin(authUrl) {
    if (this.isLoggedIn()) {
      return Promise.resolve()
    }
    const rezcavToken = AuthClient.getLocalStorageRezcavToken()
    if (rezcavToken && !this.userId()) {
      this.setAuthInterceptor(rezcavToken)

      return new Promise((resolve, reject) => {
        if (authUrl.includes('admin')) {
          const unsubscribe = onAuthStateChanged(this.auth, (user) => {
            this.loginToRezcavWithFirebase(user, authUrl).then(resolve).catch(reject).finally(unsubscribe)
          })
        } else {
          axios.get(
            routes.producerProfileUrl
          ).then((response) => {
            this.updateUser(response.data)
            resolve()
          }).catch((error) => reject())
        }
      })
    }
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(this.auth, (user) => {
        this.loginToRezcavWithFirebase(user, authUrl).then(resolve).catch(reject).finally(unsubscribe)
      })
    })
  }

  isExternallyAuthenticated() {
    return this.initialized && (!!this.auth.currentUser || !!this.state.beatstarsPassthrough)
  }

  isLoggedIn() {
    return !!this.userId() && (!!AuthClient.getLocalStorageRezcavToken() || this.isExternallyAuthenticated())
  }

  logout() {
    return new Promise((resolve, reject) => {
      AuthClient.removeLocalStorageRezcavToken()
      this.ejectAuthInterceptor()
      this.state = {
        user: {},
        beatstarsPassthrough: null,
      }
      mixpanel.reset()
      if (this.auth.currentUser) {
        signOut(this.auth).then(resolve).catch(reject)
      } else {
        resolve()
      }
    })
  }

  async refreshRezcavToken() {
    try {
      const res = await axios.post(routes.refreshTokenUrl, {
        fcm_token: this.fcmToken,
        app_version: this.appVersion,
      })
      const { token } = res.data
      AuthClient.setLocalStorageRezcavToken(token)
      this.setAuthInterceptor(token)
    } catch (error) {
      AuthClient.removeLocalStorageRezcavToken()
      this.ejectAuthInterceptor()
    }
  }

  loginToRezcav(resolve, reject, authUrl, type, token, email, name) {
    axios.post(authUrl, {
      type,
      token,
      email, // email and name are required for admin login
      name,
      fcm_token: this.fcmToken,
      app_version: this.appVersion,
    }).then((res) => {
      const { id, user_id, token, username, profile } = res.data
      AuthClient.setLocalStorageRezcavToken(token)
      this.updateUser({
        user_id: id || user_id,
        username: username,
        profile: profile
      })
      this.setAuthInterceptor(token)
      resolve()
    }).catch(reject)
  }

  loginToRezcavWithFirebase(user, authUrl) {
    if (!user || !authUrl) {
      return Promise.reject()
    }
    if (user && this.userId()) {
      return Promise.resolve()
    }
    return new Promise((resolve, reject) => {
      user.getIdToken(false).then((firebaseToken) => {
        this.loginToRezcav(resolve, reject, authUrl, 'FIREBASE', firebaseToken, user.email, user.displayName)
      })
    })
  }

  loginToRezcavWithBeatstars(beatstarsPassthrough, authUrl) {
    if (!beatstarsPassthrough || !authUrl) {
      return Promise.reject()
    }
    if (beatstarsPassthrough && this.userId()) {
      return Promise.resolve()
    }
    return new Promise((resolve, reject) => {
      this.state.beatstarsPassthrough = beatstarsPassthrough
      this.loginToRezcav(resolve, reject, authUrl, 'BEATSTARS', beatstarsPassthrough, null, null)
    })
  }

  signUp(signUpUrl, username, profile) {
    return new Promise((resolve, reject) => {
      if (this.auth.currentUser) {
        this.auth.currentUser.getIdToken(false).then((firebaseToken) => {
          this.signUpImpl(resolve, reject, signUpUrl, 'FIREBASE', firebaseToken, username, profile)
        })
      } else if (this.state.beatstarsPassthrough) {
        this.signUpImpl(resolve, reject, signUpUrl, 'BEATSTARS', this.state.beatstarsPassthrough, username, profile)
      }
    })
  }

  signUpImpl(resolve, reject, signUpUrl, type, token, username, profile) {
    axios.post(signUpUrl, {
      type,
      token,
      username,
      profile: JSON.stringify(profile),
      fcm_token: this.fcmToken,
      app_version: this.appVersion,
    }).then((res) => {
      const { user_id, token, username, profile } = res.data
      AuthClient.setLocalStorageRezcavToken(token)
      this.updateUser({
        user_id: user_id,
        username: username,
        profile: profile
      })
      this.setAuthInterceptor(token)
      resolve()
    }).catch(reject)
  }

  async configureMessaging() {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return false
    }
    this.fcmToken = await getToken(this.messaging, { vapidKey: this.firebaseConfig.messaging.vapidKey })
    this.refreshRezcavToken()
    return true
  }

  remoteConfigValue(key) {
    return getValue(this.remoteConfig, key).asString()
  }

  setAuthInterceptor(token) {
    this.ejectAuthInterceptor()
    this.state.authInterceptor = axios.interceptors.request.use((config) => {
      config.headers = Object.assign({}, config.headers, { 'Authorization': `Bearer ${token}` })
      return config
    })
  }

  ejectAuthInterceptor() {
    axios.interceptors.request.eject(this.state.authInterceptor)
  }

  static setLocalStorageRezcavToken(token) {
    window?.localStorage?.setItem('_rct', token)
  }

  static getLocalStorageRezcavToken() {
    return window?.localStorage?.getItem('_rct')
  }

  static removeLocalStorageRezcavToken() {
    window?.localStorage?.removeItem('_rct')
  }
}

export default new AuthClient()
